import React, { useMemo, useState } from "react";
import styles from "./SideBar.module.scss";
import { SavedRoute } from "../../services/api.tsx";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import BeachAccessOutlinedIcon from "@mui/icons-material/BeachAccessOutlined";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import FestivalOutlinedIcon from "@mui/icons-material/FestivalOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import HotelOutlinedIcon from "@mui/icons-material/HotelOutlined";
import LocalFloristOutlinedIcon from "@mui/icons-material/LocalFloristOutlined";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import RestaurantOutlinedIcon from "@mui/icons-material/RestaurantOutlined";
import SearchIcon from "@mui/icons-material/Search";

type LocalMapa = {
  id?: string | number;
  nome: string;
  categoria?: string | Array<{ id?: any; nomeCategoria?: string }>;
  categorias?: Array<{ id?: any; nomeCategoria?: string }>;
  cidade?: string;
  descricao?: string;
  latitude?: number;
  longitude?: number;
  fotos?: string[];
  avaliacao?: number;
  endereco?: string;
};

interface SideBarProps {
  onFilterClick?: () => void;
  isOpen: boolean;
  onToggle: () => void;
  locais?: LocalMapa[];
  selectedFilterId?: string | null;
  onSelectedFilterChange?: (filterId: string | null) => void;
  savedRoutes?: SavedRoute[];
  selectedSavedRouteId?: string | null;
  onSavedRouteChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  onItemHover?: (local: SidebarPlace) => void;
  onItemLeave?: () => void;
}

type SidebarPlace = {
  id: string;
  nome: string;
  tipo: string;
  cidade: string;
  descricao: string;
  tags?: string[];
  latitude?: number;
  longitude?: number;
  fotos?: string[];
  categorias?: Array<{ id?: any; nomeCategoria?: string }>;
  categoria?: string | Array<{ id?: any; nomeCategoria?: string }>;
  avaliacao?: number;
  endereco?: string;
};

type SidebarFilter = {
  id: string;
  label: string;
  icon: React.ElementType;
  matches: (item: SidebarPlace) => boolean;
};

const normalizeCategory = (local: LocalMapa): string => {
  if (typeof local.categoria === "string") {
    return local.categoria;
  }

  const source = Array.isArray(local.categoria)
    ? local.categoria
    : Array.isArray(local.categorias)
    ? local.categorias
    : [];

  return source
    .map((categoria) => categoria?.nomeCategoria)
    .filter(Boolean)
    .join(", ") || "Local";
};

const normalizeTags = (local: LocalMapa): string[] => {
  const source = Array.isArray(local.categoria)
    ? local.categoria
    : Array.isArray(local.categorias)
    ? local.categorias
    : [];

  return Array.from(
    new Set(
      source
        .map((categoria) =>
          typeof categoria === "string" ? categoria : categoria?.nomeCategoria,
        )
        .filter((value): value is string => Boolean(value)),
    ),
  );
};

const normalizarLocais = (locais: LocalMapa[] = []): SidebarPlace[] =>
  locais.map((local, index) => ({
    id: `local-${local.id ?? `${local.nome}-${index}`}`,
    nome: local.nome,
    tipo: normalizeCategory(local),
    cidade: local.cidade ?? "Sergipe",
    descricao: local.descricao ?? "",
    tags: normalizeTags(local),
    latitude: local.latitude,
    longitude: local.longitude,
    fotos: local.fotos,
    categorias: local.categorias,
    categoria: local.categoria,
    avaliacao: local.avaliacao,
    endereco: local.endereco,
  }));

const getSearchText = (item: SidebarPlace) =>
  [item.nome, item.tipo, item.cidade, item.descricao, ...(item.tags ?? [])]
    .join(" ")
    .toLowerCase();

const hasText = (item: SidebarPlace, ...terms: string[]) => {
  const text = getSearchText(item);
  return terms.some((term) => text.includes(term));
};

const sidebarFilters: SidebarFilter[] = [
  {
    id: "restaurantes",
    label: "Restaurantes",
    icon: RestaurantOutlinedIcon,
    matches: (item) => hasText(item, "restaurante"),
  },
  {
    id: "hoteis",
    label: "Hotéis",
    icon: HotelOutlinedIcon,
    matches: (item) => hasText(item, "hotel"),
  },
  {
    id: "pousadas",
    label: "Pousadas",
    icon: HotelOutlinedIcon,
    matches: (item) => hasText(item, "pousada"),
  },
  {
    id: "comida-tipica",
    label: "Comida típica",
    icon: RestaurantOutlinedIcon,
    matches: (item) =>
      hasText(item, "comida típica", "comida tipica", "típico", "tipico"),
  },
  {
    id: "praia",
    label: "Praia",
    icon: BeachAccessOutlinedIcon,
    matches: (item) => hasText(item, "praia", "beira-mar", "beira mar"),
  },
  {
    id: "economico",
    label: "Econômico",
    icon: HotelOutlinedIcon,
    matches: (item) => hasText(item, "econômico", "economico"),
  },
  {
    id: "ecoturismo",
    label: "Ecoturismo",
    icon: LocalFloristOutlinedIcon,
    matches: (item) =>
      hasText(item, "ecoturismo", "natureza", "trilha", "cânion", "canion"),
  },
  {
    id: "artesanato",
    label: "Artesanato",
    icon: PaletteOutlinedIcon,
    matches: (item) => hasText(item, "artesanato"),
  },
  {
    id: "centros-historicos",
    label: "Centros Históricos",
    icon: AccountBalanceOutlinedIcon,
    matches: (item) =>
      hasText(
        item,
        "histórico",
        "historico",
        "centro histórico",
        "centro historico",
      ),
  },
  {
    id: "cultura-festas",
    label: "Cultura & Festas",
    icon: FestivalOutlinedIcon,
    matches: (item) => hasText(item, "cultura", "festa", "festival"),
  },
  {
    id: "pontos-turisticos",
    label: "Pontos Turísticos",
    icon: CameraAltOutlinedIcon,
    matches: (item) => item.avaliacao === undefined,
  },
];

const getIconByStore = (item: SidebarPlace) => {
  const texto =
    `${item.tipo} ${item.descricao} ${item.tags?.join(" ") ?? ""}`.toLowerCase();

  if (texto.includes("restaurante") || texto.includes("comida")) {
    return RestaurantOutlinedIcon;
  }

  if (texto.includes("hotel") || texto.includes("pousada")) {
    return HotelOutlinedIcon;
  }

  if (texto.includes("praia")) {
    return BeachAccessOutlinedIcon;
  }

  if (texto.includes("artesanato")) {
    return PaletteOutlinedIcon;
  }

  if (texto.includes("histórico") || texto.includes("historico")) {
    return AccountBalanceOutlinedIcon;
  }

  if (texto.includes("ecoturismo") || texto.includes("natureza")) {
    return LocalFloristOutlinedIcon;
  }

  if (texto.includes("cultura") || texto.includes("festa")) {
    return FestivalOutlinedIcon;
  }

  return CameraAltOutlinedIcon;
};

const SideBar: React.FC<SideBarProps> = ({
  onFilterClick,
  isOpen,
  onToggle,
  locais = [],
  selectedFilterId,
  onSelectedFilterChange,
  savedRoutes = [],
  selectedSavedRouteId,
  onSavedRouteChange,
  onItemHover,
  onItemLeave,
}) => {
  const [internalActiveFilter, setInternalActiveFilter] = useState<string | null>(
    null,
  );
  const [searchValue, setSearchValue] = useState("");
  const searchTerm = searchValue.trim().toLowerCase();
  const activeFilter =
    selectedFilterId === undefined ? internalActiveFilter : selectedFilterId;

  const handleFilterSelect = (filterId: string) => {
    const nextFilter = activeFilter === filterId ? null : filterId;

    if (selectedFilterId === undefined) {
      setInternalActiveFilter(nextFilter);
    }

    onSelectedFilterChange?.(nextFilter);
  };

  const sidebarItems = useMemo(
    () => normalizarLocais(locais),
    [locais],
  );

  const filtersWithPlaces = sidebarFilters
    .map((filter) => {
      const places = sidebarItems.filter((item) => {
        const matchesSearch =
          !searchTerm || getSearchText(item).includes(searchTerm);
        return matchesSearch && filter.matches(item);
      });

      return { ...filter, places };
    })
    .filter((filter) => filter.places.length > 0);

  const visibleFilters =
    activeFilter && !searchTerm
      ? filtersWithPlaces.filter((filter) => filter.id === activeFilter)
      : filtersWithPlaces;

  return (
    <aside
      className={`${styles.sidebarContainer} ${
        isOpen ? styles.open : styles.closed
      }`}
      aria-label="Menu de exploração do mapa"
    >
      {!isOpen ? (
        <button
          className={styles.collapsedButton}
          onClick={onToggle}
          aria-label="Abrir menu de exploração"
          aria-expanded={false}
          type="button"
        >
          <MapOutlinedIcon fontSize="small" />
          <span>Explorar</span>
          <ChevronRightIcon fontSize="small" />
        </button>
      ) : (
        <div className={styles.sidebar}>
          <div className={styles.sidebarHeader}>
            <div className={styles.headerTop}>
              <div className={styles.logo}>
                <span className={styles.logoIcon}>
                  <MapOutlinedIcon fontSize="small" />
                </span>
                <div>
                  <div className={styles.logoTitle}>Explore</div>
                  <div className={styles.logoSubtitle}>Sergipe</div>
                </div>
              </div>
              <button
                className={styles.closeButton}
                onClick={onToggle}
                aria-label="Fechar menu"
                type="button"
              >
                <ChevronLeftIcon fontSize="small" />
              </button>
            </div>

            <div className={styles.searchContainer}>
              <SearchIcon className={styles.searchIcon} fontSize="small" />
              <input
                type="text"
                placeholder="Buscar estabelecimentos..."
                className={styles.searchInput}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
              />
              {searchValue && (
                <button
                  className={styles.searchClear}
                  onClick={() => setSearchValue("")}
                  aria-label="Limpar busca"
                  type="button"
                >
                  <CloseIcon fontSize="inherit" />
                </button>
              )}
            </div>

            {savedRoutes.length > 0 && (
              <div className={styles.routeSelector}>
                <label htmlFor="saved-route-select">Roteiros salvos</label>
                <select
                  id="saved-route-select"
                  value={selectedSavedRouteId ?? ""}
                  onChange={onSavedRouteChange}
                >
                  <option value="">Selecione um roteiro</option>
                  {savedRoutes.map((route, index) => {
                    const key = route.id ?? `local-${index}`;
                    const dateLabel = route.date
                      ? ` (${new Date(route.date).toLocaleDateString("pt-BR")})`
                      : "";

                    return (
                      <option key={key} value={key}>
                        Dia {route.dayNumber}
                        {dateLabel}
                      </option>
                    );
                  })}
                </select>
              </div>
            )}
          </div>

          <div className={styles.divider} />

          <div className={styles.sidebarBody}>
            <p className={styles.sectionLabel}>FILTROS</p>

            {visibleFilters.length === 0 ? (
              <div className={styles.emptyState}>
                <SearchIcon fontSize="large" />
                <p>Nenhum estabelecimento encontrado</p>
              </div>
            ) : (
              <ul className={styles.sidebarMenu}>
                {visibleFilters.map((filter) => {
                  const Icon = filter.icon;
                  const isOpenFilter =
                    activeFilter === filter.id || Boolean(searchTerm);

                  return (
                    <li key={filter.id}>
                      <button
                        className={`${styles.menuItem} ${
                          activeFilter === filter.id ? styles.active : ""
                        }`}
                        onClick={() => handleFilterSelect(filter.id)}
                        type="button"
                      >
                        <span className={styles.menuIcon}>
                          <Icon fontSize="small" />
                        </span>
                        <span className={styles.menuLabel}>
                          <span>{filter.label}</span>
                        </span>
                        <span className={styles.menuCount}>
                          {filter.places.length}
                        </span>
                      </button>

                      {isOpenFilter && (
                        <ul className={styles.filterResults}>
                          {filter.places.map((item) => {
                            const ItemIcon = getIconByStore(item);

                            return (
                              <li key={`${filter.id}-${item.id}`}>
                                <button
                                  className={styles.resultItem}
                                  type="button"
                                  onMouseEnter={() => onItemHover?.(item)}
                                  onMouseLeave={() => onItemLeave?.()}
                                >
                                  <span className={styles.resultIcon}>
                                    <ItemIcon fontSize="inherit" />
                                  </span>
                                  <span className={styles.resultText}>
                                    <span>{item.nome}</span>
                                    <small>
                                      {item.tipo} · {item.cidade}
                                    </small>
                                  </span>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className={styles.divider} />
          <div className={styles.sidebarFooter}>
            <button
              className={styles.filterButton}
              onClick={onFilterClick}
              type="button"
            >
              <FilterAltOutlinedIcon fontSize="small" />
              Aplicar Filtros
            </button>
            <p className={styles.footerNote}>
              {activeFilter
                ? `Filtro aberto: ${
                    sidebarFilters.find((filter) => filter.id === activeFilter)
                      ?.label ?? "filtro"
                  }`
                : "Selecione um filtro"}
            </p>
          </div>
        </div>
      )}
    </aside>
  );
};

export default SideBar;
